package com.crowpay.views.screens.common

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.completionResponse.CompletionFlowPermissions
import com.crowpay.extensions.markAsRead
import com.crowpay.extensions.requiresAction
import com.crowpay.incidentResponse.IncidentFlowPermissions
import com.crowpay.sdk.ApiOption
import com.crowpay.sdk.UnreadHandler
import com.crowpay.sdk.currentSession
import com.crowpay.sdk.notNullSession
import com.crowpay.sdk.selectedApi
import com.crowpay.utils.*
import com.crowpay.views.components.actionIcon
import com.crowpay.views.components.activityIcon
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.components.project.*
import com.crowpay.views.components.project.activity.concerns.concerns
import com.crowpay.views.components.project.activity.concerns.hasConcernCard
import com.crowpay.views.components.project.activity.projectMessages
import com.crowpay.views.components.project.activity.projectToDos
import com.crowpay.views.components.project.projectInfo.contacts
import com.crowpay.views.components.project.projectInfo.ledger
import com.crowpay.views.components.project.projectInfo.projectDetails
import com.crowpay.views.components.project.work.changeOrders.projectChangeOrders
import com.crowpay.views.components.project.work.payApps.DirectlyDisplayableState
import com.crowpay.views.components.project.work.payApps.draws
import com.crowpay.views.components.project.work.punchList.projectTasks
import com.crowpay.views.components.project.work.scope.scopeV3
import com.crowpay.views.components.unreadIconWhen
import com.crowpay.views.screens.auth.LogInScreen
import com.crowpay.views.screens.client.ClientProject
import com.crowpay.views.screens.contractor.ContractorProject
import com.crowpay.views.screens.contractor.MightBeContractorPage
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.GreyedOutSemantic
import com.crowpay.views.theming.InnerTabBar
import com.crowpay.views.theming.OuterTabBar
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.colored
import com.crowpay.views.theming.specCenteredContent
import com.crowpay.views.theming.textButton
import com.lightningkite.UUID
import com.lightningkite.kiteui.ConsoleRoot
import com.lightningkite.kiteui.QueryParameter
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.Color
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlin.collections.associateWith
import kotlin.collections.emptyList

@Serializable
enum class ProjectLens {
    Contractor,
    Customer,
    Preview
    ;

    fun isClient() = this != Contractor
}

enum class ActionImportance(
    override val displayName: String,
    override val color: Color,
    override val priority: Int,
    val icon: Icon
) : DirectlyDisplayableState {
    Important("Important", AppColors.yellow.main, 0, Icon.activity.resize(1.rem)),
    Critical("Critical", AppColors.red.main, 1, Icon.activity.resize(1.rem));
}

@Routable("/projects/{id}")
class ProjectRouter(val id: UUID) : Page {
    @QueryParameter
    val jumpto = Property<URLs.JumpTo?>(null)

    override fun ViewWriter.render2(): ViewModifiable = stack {
        launch {
            println("Routing project, jumpto = ${jumpto.value}")
            val routeTo = when {
                currentSession() == null -> LogInScreen().apply {
                    nextScreen = ProjectRouter(id).also { it.jumpto.value = jumpto.value }
                }

                selectedContractor() != null -> ContractorProject(id).also { it.jumpLocation.value = jumpto.value }
                else -> ClientProject(id).also { it.jumpLocation.value = jumpto.value }
            }
            screenNavigator.replace(routeTo)
        }

        centered - activityIndicator()
    }
}

private typealias ProjectSubView = ProjectView.(ViewWriter) -> ViewModifiable
typealias ProjectMap<T> = Map<ProjectView.ProjectGroups, Map<ProjectView.ProjectSection, T>>

operator fun <T> ProjectMap<T>.get(group: ProjectView.ProjectGroups, section: ProjectView.ProjectSection) = get(group)?.get(section)

// This interface is to reuse the same cached queries when navigating into detail screens for a project
interface HasProjectData {
    val projectId: UUID
    val project: Readable<Project>
    val lineItems: Readable<List<AdjustedLineItem>>
    val contractorNotes: Readable<List<ContractorNote>>
    val client: Readable<Client>
    val contractor: Readable<Contractor>
    val punchListItems: Readable<List<PunchListItem>>
    val changeOrders: Readable<List<ChangeRequest>>
    val disputes: Readable<List<Dispute>>
    val scopes: Readable<List<ScopeView>>
    val pendingItemChanges: Readable<List<PendingItemChange>>
    val pendingLineItems: Readable<List<PendingLineItem>>
    val payApps: Readable<List<PayApplication>>
    val payAppItems: Readable<List<PayAppItem>>
    val lineItemCompletionMessages: Readable<List<LineItemCompletionMessage>>
    val incidents: Readable<List<Incident>>
    val incidentMessages: Readable<List<IncidentMessage>>
    val unreadMessages: Readable<List<ProjectMessage>>
    val lineItemCompletionMessageComments: Readable<List<CompletionMessageComment>>
    val incidentMessageComments: Readable<List<IncidentMessageComment>>
}

class UnauthorizedLensException(val viewingAs: ProjectLens) : Exception(
    when (viewingAs) {
        ProjectLens.Contractor -> "a contractor"
        ProjectLens.Customer -> "the homeowner"
        ProjectLens.Preview -> "anonymous"
    }.let {
        "You are attempting to view this page as $it, which is forbidden. You must be $it of the project to view as $it."
    }
)

suspend fun HasProjectData.assertViewerLens(lens: ProjectLens) {
    val self = notNullSession().self()
    if (self.role >= UserRole.Admin) return

    val project = project()
    when (lens) {
        ProjectLens.Contractor -> if (project.contractor != self.membership?.contractor) throw UnauthorizedLensException(lens)
        ProjectLens.Customer -> if (project.customer != null && project.customer != self._id) throw UnauthorizedLensException(lens)
        ProjectLens.Preview -> if (project.contractor != self.membership?.contractor && project.customer != null) throw UnauthorizedLensException(lens)
    }
}

abstract class ProjectView(override val lens: ProjectLens) : MightBeContractorPage, HasProjectData {
    sealed interface ProjectSection : HasDisplayName {
        val render: ProjectSubView
    }

    @Serializable
    enum class WorkSection(override val displayName: String, override val render: ProjectSubView) : ProjectSection {
        Scope("Scope", ProjectView::scopeV3),
        Tasks("Tasks", { it.projectTasks(project, lineItems, punchListItems, lens) }),
        ChangeOrders("Changes", ProjectView::projectChangeOrders),
        PayApps("Draws", ProjectView::draws),
    }

    @Serializable
    enum class InfoSection(override val displayName: String, override val render: ProjectSubView) : ProjectSection {
        Ledger("Ledger", ProjectView::ledger),
        Details("Details", ProjectView::projectDetails),
        Contacts("Contacts", ProjectView::contacts)
    }

    @Serializable
    enum class ActivitySection(override val displayName: String, override val render: ProjectSubView) : ProjectSection {
        ToDos("To-Do's", ProjectView::projectToDos),
        Concerns("Concerns", ProjectView::concerns),
        Messaging("Messaging", ProjectView::projectMessages),
    }


    @Serializable
    enum class RegisterAccountSection(val displayName: String) {
        Register("Primary User"),
        Operations("Business Operations"),
        Profile("Business Profile"),
        Verification("Business Verification"),
        Owners("Owners"),
        Bank("Connect Bank"),
        Alerts("Alerts"),
        Users("Users"),
        Specs("Specifications")
    }

    class PinnableTab<T>(val name: String, serializer: KSerializer<T>, val default: T) : ImmediateWritable<T> {
        val pinned = PersistentProperty("$name-pinned-tab", default, serializer)
        val selected = Property(pinned.value)

        fun pinSelected() {
            pinned.value = selected.value
        }

        val selectedIsPinned = shared { selected() == pinned() }.withWrite { if (it) pinSelected() }

        // Just defer to the selected tab
        override var value: T by selected::value
        override fun addListener(listener: () -> Unit): () -> Unit = selected.addListener(listener)
        override val state: ReadableState<T> get() = selected.state
    }

    val selectedInfoTab = PinnableTab("info", InfoSection.serializer(), InfoSection.Details)
    val selectedActivityTab = PinnableTab("activity", ActivitySection.serializer(), ActivitySection.ToDos)
    val selectedWorkTab = PinnableTab("work", WorkSection.serializer(), WorkSection.Scope)


    @Serializable
    enum class ProjectGroups(override val displayName: String, val sections: List<ProjectSection>) :
        HasDisplayName {
        Overview("Overview", InfoSection.entries),
        Activity("Activity", ActivitySection.entries),
        Work("Work", WorkSection.entries)
    }

    val selectedTabGroup = PinnableTab("group", ProjectGroups.serializer(), ProjectGroups.Work)

    open suspend fun ViewWriter.onAccept() {}
    open suspend fun ViewWriter.onLogin() {}
    open suspend fun ViewWriter.onRegister() {}

    private val cache = HashMap<String, Any?>()
    fun <T> caching(key: String, getter: () -> T): T =
        try {
            cache.getOrPut(key, getter) as T
        } catch (e: ClassCastException) {
            throw IllegalArgumentException("ProjectView cache key $key has multiple conflicting types", e)
        }

    override val title: Readable<String> = shared { project().name.truncate(15) }

    //------------
    // Visibility
    //------------
    val anonymous = shared {
        val p = project()
        lens == ProjectLens.Preview &&
                (p.state == ProjectState.WaitingApproval || p.state == ProjectState.Invited) &&
                currentSession() == null
    }
    val needsFundingRequested = shared {
        project().let { it.state == ProjectState.Accepted && it.fullFundingRequested == null && it.fundingNeeded == 0L }
    }
    val fundingNeeded = shared { project().fundingNeeded > 0 }
    val inSubstantialCompletion = shared {
        project().state == ProjectState.SubstantialCompletion
    }
    val requestedComplete = shared {
        project().state == ProjectState.RequestingComplete
    }
    val canRequestComplete = shared { lens == ProjectLens.Contractor && inSubstantialCompletion() }
    val canAcceptComplete = shared { lens == ProjectLens.Customer && requestedComplete() }
    val canMakePayment = shared { lens == ProjectLens.Customer && fundingNeeded() }
    val canRequestFunding = shared { lens == ProjectLens.Contractor && needsFundingRequested() }
    val canCancelFundingRequest =
        shared { lens == ProjectLens.Contractor && project().state == ProjectState.AwaitingFunding }
    val canAbandonProject = shared {
        val p = project()
        lens == ProjectLens.Contractor &&
                (p.state == ProjectState.WaitingApproval || p.state == ProjectState.Invited)
    }
    val canCancelProject = shared {
        val p = project()
        p.state >= ProjectState.Accepted &&
                p.state <= ProjectState.AwaitingFunding
    }
    val canAcceptProject = shared {
        val p = project()
        lens == ProjectLens.Preview &&
                (p.state == ProjectState.WaitingApproval || p.state == ProjectState.Invited) &&
                currentSession() != null
    }
    val ongoingIncidents = shared { incidents().filter { it.resolved == null } }

    val pullMethods = BasicListenable()
    val needsPaymentMethods: Readable<Boolean> = sharedSuspending {
        rerunOn(pullMethods)
        val session = currentSession()
        session != null && session.nonCached.userAuth.getPaymentMethods().isEmpty()
    }

    private fun AdjustedLineItem.requiresReview() =
        if (lens == ProjectLens.Contractor) state == LineItemState.NotApproved
        else state == LineItemState.RequestingReview

    val itemsRequiringReview: Readable<List<AdjustedLineItem>> = shared {
        lineItems().filter { it.scopeSet != true && it.requiresReview() }
    }

    val scopesRequiringReview: Readable<List<ScopeView>> = shared {
        val review = lineItems()
            .distinctBy { it.scopeView }
            .filter { it.requiresReview() }
            .mapNotNull { it.scopeView }

        scopes().filter { it._id in review }
    }

    val rejectedItems = shared {
        lineItems().filter { it.state == LineItemState.NotApproved }
    }

    val pendingChanges: Readable<List<ChangeRequestItem>> =
        if (lens == ProjectLens.Customer)
            shared {
                val published = changeOrders().filter { it.published != null }.map { it._id }
                pendingItemChanges().filter { it.pending && it.changeRequest in published } +
                        pendingLineItems().filter { it.pending && it.changeRequest in published }
            }
        else Constant(emptyList())

    val todo = shared {
        var n = 0
        val debugger: StringBuilder? = null
        debugger?.appendLine("TODO:")
        fun addToDo(name: String, amount: Int) {
            if (amount > 0) {
                debugger?.append("- $name")
                if (amount > 1) debugger?.append(" ($amount)")
                debugger?.append('\n')
                n += amount
            }
        }
        if (canAcceptProject()) addToDo("accept project", 1)
        if (canMakePayment()) addToDo("make payment", 1)
        if (canRequestFunding()) addToDo("request funding", 1)
        if (canMakePayment() && needsPaymentMethods()) addToDo("make payment and setup method", 1)
        if (lens == ProjectLens.Contractor && project().state == ProjectState.PendingStart) addToDo("start project", 1)

        if (lens == ProjectLens.Contractor) {
            addToDo("rejected work items", rejectedItems().size)
        }

        if (lens == ProjectLens.Customer) {
            addToDo("work items needing review", itemsRequiringReview().size)
            addToDo("scopes needing review", scopesRequiringReview().size)
            addToDo("pending item changes", pendingChanges().size)
            addToDo("pending draws", payAppItems().distinctBy { it.payApp }.count { it.pending })
        }

        if (debugger != null) println(debugger.toString())

        n
    }

    val workItemPermissions = shared {
        val p = project()
        val messages = lineItemCompletionMessages().groupBy { it.lineItem }
        lineItems().associate {
            it._id to CompletionFlowPermissions(it.wraps, lens.isClient(), messages[it._id] ?: emptyList(), p.preSubstantialCompletion)
        }
    }

    val incidentPermissions = shared {
        val p = project()
        val messages = incidentMessages().groupBy { it.incident }
        incidents().associate {
            it._id to IncidentFlowPermissions(p, it, messages[it._id] ?: emptyList(), lens.isClient())
        }
    }

    private val flowPermissions = shared { workItemPermissions().values.filter { it.messages.isNotEmpty() } + incidentPermissions().values.filter { it.messages.isNotEmpty() } }

    private val linesWithConcerns = shared {
        val allPerms = workItemPermissions()
        lineItems()
            .filter { it.hasConcernCard(allPerms[it._id]?.messages ?: emptyList()) }
            .map { it._id }
            .toSet()
    }

    val unreadTabs = currentSession.lens { session ->
        if (session == null) return@lens emptyMap()

        ProjectGroups.entries.associateWith { group ->
            group.sections.associateWith { section ->
                when (section) {
                    InfoSection.Ledger -> UnreadHandler.Never
                    InfoSection.Contacts -> UnreadHandler.Never
                    InfoSection.Details -> session.contractorNotes.handle { contractorNotes().filter { it.lineItem == null } }

                    ActivitySection.ToDos -> UnreadHandler.Never

                    ActivitySection.Concerns ->
                        session.lineItemCompletionMessages.handle { lineItemCompletionMessages().filter { it.lineItem in linesWithConcerns() } } +
                                session.incidentMessages.handle { incidentMessages() } +
                                session.completionMessageComments.handle { lineItemCompletionMessageComments().filter { it.lineItem in linesWithConcerns() } } +
                                session.payAppItems.handleFeedback { payAppItems() } +
                                session.pendingItemChange.handleFeedback { pendingItemChanges() } +
                                session.pendingLineItems.handleFeedback { pendingLineItems() }

                    ActivitySection.Messaging -> session.projectMessages.handle { unreadMessages() }


                    WorkSection.Scope ->
                        session.scopeViews.handle(ConsoleRoot) { scopes() } +
                                session.lineItems.handle(ConsoleRoot) { lineItems().map { it.wraps } } +
                                session.itemChanges.handle(ConsoleRoot) { lineItems().flatMap { it.changes } } +
                                session.contractorNotes.handle(ConsoleRoot) { contractorNotes().filter { it.lineItem != null } } +
                                session.punchLists.handle(ConsoleRoot) { punchListItems().filter { it.lineItem != null } } +
                                session.lineItems.handleCredits { lineItems().map { it.wraps } }

                    WorkSection.Tasks -> session.punchLists.handle { punchListItems().filter { it.lineItem == null } }

                    WorkSection.ChangeOrders ->
                        session.changeRequests.handle { changeOrders() } +
                                session.pendingLineItems.handle { pendingLineItems() } +
                                session.pendingItemChange.handle { pendingItemChanges() }

                    WorkSection.PayApps -> session.payApplications.handle { payApps() }
                }
            }
        }
    }

    val tabActionIcons = shared<ProjectMap<ActionImportance?>>(useLastWhileLoading = true) {
        ProjectGroups.entries.associateWith { group ->
            group.sections.associateWith { section ->
                when (section) {
                    ActivitySection.Concerns -> flowPermissions().mapNotNull { it.requiresAction(concernsOnly = true) }.maxOrNull()
                    WorkSection.Scope -> if (lens.isClient() && lineItems().any { it.state == LineItemState.RequestingReview }) ActionImportance.Important else null
                    WorkSection.ChangeOrders -> if (lens.isClient() && pendingChanges().isNotEmpty()) ActionImportance.Important else null
                    WorkSection.PayApps -> if (lens.isClient() && payAppItems().any { it.pending }) ActionImportance.Important else null
                    ActivitySection.ToDos -> if (todo() > 0) ActionImportance.Important else null
                    else -> null
                }
            }
        }
    }

    //-------
    // Views
    //-------

    val selectedTab = shared<PinnableTab<out ProjectSection>> {
        when (selectedTabGroup()) {
            ProjectGroups.Overview -> selectedInfoTab
            ProjectGroups.Activity -> selectedActivityTab
            ProjectGroups.Work -> selectedWorkTab
        }
    }

    val selectedSection = shared<ProjectSection> { selectedTab()() }.withImmediateWrite {
        when (it) {
            is InfoSection -> {
                selectedTabGroup.value = ProjectGroups.Overview
                selectedInfoTab.value = it
            }

            is ActivitySection -> {
                selectedTabGroup.value = ProjectGroups.Activity
                selectedActivityTab.value = it
            }

            is WorkSection -> {
                selectedTabGroup.value = ProjectGroups.Work
                selectedWorkTab.value = it
            }
        }
    }

    fun ViewWriter.projectView() =
        scrolls - col {
            launch {
                notNullSession().projects.markAsRead(project())
            }

            launch {
                if (this@ProjectView !is DevProjectView.UnsafeProjectView) assertViewerLens(lens)
            }

            registerJumpTos()
            reactiveSuspending {
                jumpLocation()?.let {
                    delay(500) // jump tos get f'd up when navigating around a lot, the timings are really hard to figure out so this is a patch fix
                    JumpTo.jumpFromUrl(
                        it,
                        vertical = when (it.section) {
                            JumpSection.ProjectMessages -> Align.End
                            JumpSection.LineItem -> Align.Start
                            else -> Align.Start
                        }
                    )
                }
            }

            sizeConstraints(width = AppDimensions.pageWidth) - col {
                spacing = AppDimensions.majorColumnSpacing

                header(this)

                disputeBar(this)

                projectLevelMessages(this)

                compact - row {
                    existsDefaultFalse { todo() > 0 }
                    centered - activityIcon()
                    centered - body {
                        ::content { "To-Do (${todo()}) - You have required To-Do's" }
                    }
                    centered - textButton - button {
                        bodyBold("View")
                        onClick { JumpTo.ToDos() }
                    }
                }

                // Visual separation
                actionBar2(this)

                if (lens == ProjectLens.Preview) previewInformation(this)

                col {
                    col {
                        spacing = 0.2.rem

                        OuterTabBar.onNext - row {
                            for (group in ProjectGroups.entries) {
                                if (viewIsNarrow) expanding
                                buttonTheme - radioToggleButton {
                                    val content: RView.() -> RowOrCol = {
                                        row {
                                            spacing = AppDimensions.expandButtonSpace
                                            centered - unreadIconWhen { unreadTabs()[group]?.any { it.value() } ?: false }
                                            centered - actionIcon { tabActionIcons()[group]?.values?.filterNotNull()?.maxByOrNull { it.priority } }
                                            centered - body(group.displayName)
                                        }
                                    }
                                    if (viewIsNarrow) centered - content()
                                    else specCenteredContent { content() }
                                    checked bind selectedTabGroup.equalTo(group)
                                }
                            }
                        }
                        colored(AppColors.primary.main) - separator()
                        row {
                            val groupSections = shared { selectedTabGroup().sections }
                            if (viewIsNarrow) expanding
                            InnerTabBar.onNext - row {
                                forEach(groupSections) { section ->
                                    if (viewIsNarrow) weight(section.displayName.length / 6f)
                                    buttonTheme - radioToggleButton {
                                        val content: RView.() -> RowOrCol = {
                                            row {
                                                spacing = AppDimensions.expandButtonSpace
                                                centered - unreadIconWhen { unreadTabs()[selectedTabGroup(), section]?.invoke() == true }
                                                centered - actionIcon { tabActionIcons()[selectedTabGroup(), section] }
                                                centered - body(section.displayName)
                                            }
                                        }
                                        if (viewIsNarrow) centered - content()
                                        else specCenteredContent { content() }
                                        checked bind selectedSection.equalTo(section)
                                    }
                                }
                            }
                            if (viewIsWide) row {
                                centered - radioToggleButton {
                                    existsDefaultFalse { selectedApi() in setOf(ApiOption.Local) }
                                    spacing = 0.25.rem
                                    val isPinned = selectedTab.flatLens { it.selectedIsPinned }

                                    checked bind isPinned
                                    icon {
                                        source = Icon.pin
                                        dynamicTheme { if (!isPinned()) GreyedOutSemantic else null }
                                    }
                                }
                                onlyWhen(false) { selectedApi() != ApiOption.Staging } - centered - textButton - button {
                                    body("Mark Tab As Read")
                                    ::action { unreadTabs()[selectedTabGroup(), selectedSection()]?.markAllAsReadAction }
                                }
                                onlyWhen(false) { selectedApi() != ApiOption.Staging } - centered - textButton - button {
                                    body("Mark Tab As Unread")
                                    ::action { unreadTabs()[selectedTabGroup(), selectedSection()]?.markAllAsUnreadAction }
                                }
                            }
                        }
                    }

                    sizeConstraints(minHeight = 35.rem) - stack {
                        reactive {
                            clearChildren()
                            selectedSection().render(this@ProjectView, this@stack)
                        }
                    }
                }

                actionBar2(this)
            }
        }

    val jumpLocation: Property<URLs.JumpTo?> = Property(null)

    private fun ViewWriter.registerJumpTos() {
        // Actions for scrolling items into view
        infix fun JumpTo.Handler.routesTo(section: ProjectSection) =
            addListener { selectedSection.setImmediate(section) }.also(::onRemove)

        JumpTo.LineItem routesTo WorkSection.Scope
        JumpTo.PendingChangeOrder routesTo WorkSection.ChangeOrders
        JumpTo.ChangeItem routesTo WorkSection.ChangeOrders
        JumpTo.PayApp routesTo WorkSection.PayApps
        JumpTo.ScopeView routesTo WorkSection.Scope

        JumpTo.ProjectDetails routesTo InfoSection.Details
        JumpTo.ProjectLedger routesTo InfoSection.Ledger

        JumpTo.ToDos routesTo ActivitySection.ToDos
        JumpTo.ProjectMessages routesTo ActivitySection.Messaging
    }

    // Has to be separated out for anonymous viewing
    override fun ViewWriter.renderMainContent(): ViewModifiable = projectView()
}